import { useEffect, useRef, useState } from 'react';
import {
  auth,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from '../firebase.js';
import { Modal, submitForm } from './Modal.jsx';
import { FormField } from './FormField.jsx';

// Normalizes a user-typed US-style number to E.164. Accepts anything that
// already has a + prefix untouched. Otherwise strips non-digits and assumes
// US (+1) if 10 digits, or if 11 digits starting with 1.
function toE164(input) {
  const s = String(input || '').trim();
  if (s.startsWith('+')) return s.replace(/\s+/g, '');
  const digits = s.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return ''; // signal invalid
}

export function MfaEnrollModal({ open, onClose, onSuccess }) {
  // Flow stages: 'input-phone' → 'reauth' → 'input-code' → 'done'
  const [stage, setStage] = useState('input-phone');
  const [phoneRaw, setPhoneRaw] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const recaptchaRef = useRef(null);
  const verifierRef = useRef(null);

  // Instantiate an invisible reCAPTCHA once the modal opens. Firebase's
  // phone-auth internals require it — the user sees no widget.
  useEffect(() => {
    if (!open) return;
    // Recreate on each open so a stale verifier from a cancelled attempt
    // doesn't linger.
    if (recaptchaRef.current && !verifierRef.current) {
      try {
        verifierRef.current = new RecaptchaVerifier(auth, recaptchaRef.current, {
          size: 'invisible',
        });
      } catch (e) {
        setErr(`Could not initialize verifier: ${e.message}`);
      }
    }
    return () => {
      if (verifierRef.current) {
        try { verifierRef.current.clear(); } catch { /* noop */ }
        verifierRef.current = null;
      }
    };
  }, [open]);

  function reset() {
    setStage('input-phone');
    setPhoneRaw(''); setCurrentPassword(''); setCode('');
    setVerificationId(null); setBusy(false); setErr(null);
  }
  function close() { reset(); onClose(); }

  async function sendSms(phoneE164) {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in.');
    if (!verifierRef.current) throw new Error('reCAPTCHA not ready.');
    const session = await multiFactor(user).getSession();
    const provider = new PhoneAuthProvider(auth);
    const vid = await provider.verifyPhoneNumber(
      { phoneNumber: phoneE164, session },
      verifierRef.current,
    );
    return vid;
  }

  async function submitPhone(e) {
    e.preventDefault();
    setErr(null);
    const phone = toE164(phoneRaw);
    if (!phone) { setErr('Enter a valid phone number.'); return; }
    setBusy(true);
    try {
      const vid = await sendSms(phone);
      setVerificationId(vid);
      setStage('input-code');
    } catch (e) {
      if (e?.code === 'auth/requires-recent-login') {
        setStage('reauth');
      } else {
        setErr(prettify(e));
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitReauth(e) {
    e.preventDefault();
    setErr(null);
    const user = auth.currentUser;
    if (!user?.email) { setErr('No signed-in user.'); return; }
    setBusy(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, cred);
      // Re-send SMS now that we're freshly authed.
      const vid = await sendSms(toE164(phoneRaw));
      setVerificationId(vid);
      setStage('input-code');
    } catch (e) {
      setErr(prettify(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e) {
    e.preventDefault();
    setErr(null);
    if (!verificationId) { setErr('Missing verification — restart the flow.'); return; }
    if (!/^\d{6}$/.test(code)) { setErr('Enter the 6-digit code from the SMS.'); return; }
    setBusy(true);
    try {
      const cred = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await multiFactor(auth.currentUser).enroll(assertion, 'Phone');
      setStage('done');
      onSuccess?.();
      setTimeout(close, 800);
    } catch (e) {
      setErr(prettify(e));
    } finally {
      setBusy(false);
    }
  }

  let title = 'Set up two-factor';
  let formId = 'mfa-phone';
  let content;
  if (stage === 'input-phone') {
    content = (
      <form id="mfa-phone" onSubmit={submitPhone} className="space-y-4">
        <p className="text-sm text-ink-2">
          We'll text a 6-digit code to confirm your number. Standard SMS rates apply.
        </p>
        <FormField label="Phone number" required hint="US numbers can be entered any format. Others: use +country code (e.g. +44…).">
          <input
            type="tel"
            className="input"
            value={phoneRaw}
            onChange={(e) => setPhoneRaw(e.target.value)}
            placeholder="(555) 123-4567"
            autoFocus
            required
          />
        </FormField>
      </form>
    );
  } else if (stage === 'reauth') {
    formId = 'mfa-reauth';
    title = 'Confirm your password';
    content = (
      <form id="mfa-reauth" onSubmit={submitReauth} className="space-y-4">
        <p className="text-sm text-ink-2">For security, confirm your password to finish setup.</p>
        <FormField label="Current password" required>
          <input
            type="password"
            className="input"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
            required
          />
        </FormField>
      </form>
    );
  } else if (stage === 'input-code') {
    formId = 'mfa-code';
    title = 'Enter the code';
    content = (
      <form id="mfa-code" onSubmit={submitCode} className="space-y-4">
        <p className="text-sm text-ink-2">
          We sent a 6-digit code to <span className="font-mono">{toE164(phoneRaw)}</span>.
        </p>
        <FormField label="Code" required>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            className="input font-mono text-center tracking-[0.3em]"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
            required
          />
        </FormField>
        <button
          type="button"
          className="text-xs text-brand underline underline-offset-2"
          onClick={async () => {
            setErr(null); setBusy(true);
            try {
              const vid = await sendSms(toE164(phoneRaw));
              setVerificationId(vid);
              setCode('');
            } catch (e) {
              setErr(prettify(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Didn't get it? Resend.
        </button>
      </form>
    );
  } else if (stage === 'done') {
    content = <p className="text-sm text-success">✓ Two-factor enabled.</p>;
  }

  return (
    <Modal
      open={open}
      onClose={close}
      eyebrow="Security"
      title={title}
      size="md"
      footer={
        stage === 'done' ? null : (
          <>
            <button type="button" onClick={close} className="btn-ghost">Cancel</button>
            <button
              type="button"
              onClick={() => submitForm(formId)}
              className="btn-primary"
              disabled={busy}
            >
              {busy ? 'Working…' : stage === 'input-code' ? 'Verify' : stage === 'reauth' ? 'Confirm' : 'Send code'}
            </button>
          </>
        )
      }
    >
      {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
      {content}
      <div ref={recaptchaRef} className="hidden" />
    </Modal>
  );
}

function prettify(e) {
  const code = e?.code || '';
  const map = {
    'auth/invalid-phone-number': 'That phone number looks invalid.',
    'auth/invalid-verification-code': 'That code is incorrect. Try again.',
    'auth/code-expired': 'Code expired. Request a new one.',
    'auth/too-many-requests': 'Too many attempts. Wait a few minutes.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-credential': 'Incorrect password.',
    'auth/operation-not-allowed': 'Phone sign-in isn\'t enabled on this project. Ask the admin.',
    'auth/second-factor-already-in-use': 'This phone is already a factor on another account.',
    'auth/maximum-second-factor-count-exceeded': 'You have the maximum number of factors enrolled.',
    'auth/unsupported-first-factor': 'Your initial sign-in method can\'t be used with MFA.',
    'auth/unverified-email': 'Verify your email before enabling two-factor.',
  };
  return map[code] || e?.message || 'Something went wrong.';
}
